import React, { MouseEvent } from 'react';
import { Menu } from 'antd';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { MeasureType, RefLocationType } from 'app/model/CORE';
import {
    AddToCustomLayerEvent,
    NewCustomLayerEvent,
    OpenTvMonitoredEvents,
    RemoveFromCustomLayerEvent,
    RemoveFromWorkspaceLayerEvent,
    ShowModalEvent,
} from 'app/common/eventbus/Events';
import { MeasureLoadProps } from 'app/components/dashboard/network/flows/MeasureLoad';
import AppContext from 'app/common/context/AppContext';
import { CustomLayerUtils, MapLayer, MapLayerUtils, MapObjectType } from 'app/components/dashboard/network/map/Map';
import Utils from 'app/common/util/Utils';
import { Flex } from 'app/components/common/Flex';
import { MeasureLoadBar } from 'app/components/dashboard/network/flows/svg/MeasureLoadBar';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { ApplicableScenarioMenuUtils, ScenarioMenuState } from '../../../common/ApplicableScenarioMenuUtils';
import { ItemType } from 'antd/lib/menu/hooks/useItems';

interface MapMeasureContextMenuProps extends ObjectTypeContextMenu {
    measures: Array<MeasureLoadProps>;
    workspaceMeasureIds: Array<string>;
    onClose(): void;
}

interface MapMeasureContextMenuState extends ScenarioMenuState {}

const ACTION_OPEN_EDIT = 'open-edit';
const ACTION_SLOT_LIST = 'slot-list';
const ACTION_OP_LOG = 'oplog';
const ACTION_INTERACTION_STUDY = 'interaction-study';
const ACTION_CREATE_CUSTOM_LAYER = 'create-custom-layer';
const ACTION_ADD_NOTE = 'add-note';
const ACTION_ADD_EVENT = 'add-event';
const ACTIONS = [
    ACTION_OPEN_EDIT,
    ACTION_SLOT_LIST,
    ACTION_OP_LOG,
    ACTION_INTERACTION_STUDY,
    ACTION_CREATE_CUSTOM_LAYER,
    ACTION_ADD_NOTE,
    ACTION_ADD_EVENT,
];

export class MapMeasureContextMenu extends React.PureComponent<MapMeasureContextMenuProps, ScenarioMenuState> {
    constructor(props: MapMeasureContextMenuProps) {
        super(props);
        this.state = {
            isApplicableScenarioLoading: false,
        };
    }

    render() {
        return (
            <Menu
                key="map-measure-context-menu"
                items={this.getMenuItems()}
                onClick={this.handleOnClick}
                triggerSubMenuAction="click"
            />
        );
    }

    getMenuItems = () => {
        const subMenu = this.props.measures?.map(measureLoadProps => {
            const notes = measureLoadProps.monitoredEvents?.filter(MonitoredEventUtils.isNoteEvent);
            const events = measureLoadProps.monitoredEvents?.filter(MonitoredEventUtils.isWeatherEvent);
            const measureId = measureLoadProps.monitoredMeasure.measure.measureId.id;
            const tv = MeasureUtils.getTvLocation(measureLoadProps.monitoredMeasure.measure);
            const measureMenuItems: ItemType[] = [];
            if (!AuthorisationUtils.isFmpMapUser()) {
                measureMenuItems.push(
                    {
                        label: measureId,
                        key: `measureload-${measureId}`,
                        className: 'menuItemLoadGraph',
                        disabled: true, //otherwise when clicking it hides the menu
                    },
                    { key: 'divider-1', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_OPEN_EDIT, measureId),
                        disabled: measureLoadProps.monitoredMeasure.measure.measureId?.type === MeasureType.REROUTING,
                        label: `Open/edit ${measureLoadProps.monitoredMeasure.measure.measureId?.id}`,
                    },
                    {
                        key: this.actionKey(ACTION_SLOT_LIST, measureId),
                        disabled:
                            !AppContext.isNmvpBaseline() ||
                            measureLoadProps.monitoredMeasure.measure.measureId?.type === MeasureType.REROUTING,
                        label: 'Show slot list',
                    },
                    {
                        key: this.actionKey(ACTION_OP_LOG, measureId),
                        label: 'Show Op Log',
                    },
                    { key: 'divider-2', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_INTERACTION_STUDY, measureId),
                        label: 'Interaction study',
                        disabled: measureLoadProps.monitoredMeasure.measure.measureId?.type === MeasureType.REROUTING,
                    },
                );
                if (tv.refLocation?.refType === RefLocationType.AS) {
                    measureMenuItems.push(
                        { key: 'divider-3', type: 'divider' },
                        ApplicableScenarioMenuUtils.applicableScenarioMenu(
                            [tv.id],
                            measureLoadProps.monitoredMeasure.measure.applicability,
                            this.state,
                            (state: MapMeasureContextMenuState) => this.setState(state),
                        ),
                    );
                }
                measureMenuItems.push(
                    { key: 'divider-4', type: 'divider' },
                    {
                        label: 'Create a custom layer',
                        key: this.actionKey(ACTION_CREATE_CUSTOM_LAYER, measureId),
                    },
                    this.addToCustomLayerMenu(measureLoadProps),
                    ...this.removeFromCustomLayerMenu(measureId),
                    ...this.removeFromWorkspaceLayerMenu(measureId),
                    { key: 'divider-5', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_ADD_NOTE, measureId),
                        label: `${Utils.isNotEmpty(notes) ? 'Show notes' : 'Add a note'}`,
                    },
                    {
                        key: this.actionKey(ACTION_ADD_EVENT, measureId),
                        label: `${Utils.isNotEmpty(events) ? 'Show events' : 'Add an event'}`,
                    },
                );
            }
            return {
                label: this.getMeasureNameLabel(measureLoadProps),
                key: measureId,
                children: measureMenuItems,
            };
        });
        if (this.props.group) {
            return [
                {
                    label: <span className="detailed-subpanel-title">Measure ({this.props.measures.length})</span>,
                    key: 'measures',
                    children: subMenu,
                },
            ];
        } else {
            return subMenu;
        }
    };

    getMeasureCustomLayers(measureId: string) {
        return CustomLayerUtils.customLayers.filter(
            customLayer =>
                customLayer.objectsToDisplay.filter(
                    objectToDisplay =>
                        MapObjectType.MEASURE === objectToDisplay.type &&
                        measureId === MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(objectToDisplay.object),
                )?.length > 0,
        );
    }

    getMeasureNameLabel(measureLoadProps: MeasureLoadProps) {
        const monitoredPeriod = AppContext.getMonitoringPeriod();
        return (
            <div onClick={e => this.handleMeasureLoadClick(e, measureLoadProps.monitoredMeasure.measure.measureId.id)}>
                <Flex grow={false}>
                    <Flex size="6.5em">
                        ID:{' '}
                        <span style={{ color: 'white' }}>{measureLoadProps.monitoredMeasure.measure.measureId.id}</span>
                    </Flex>
                    <Flex direction="vertical" style={{ alignSelf: 'center', paddingLeft: '1em' }}>
                        <MeasureLoadBar
                            key="regulation-load"
                            loadValues={measureLoadProps.monitoredMeasure.measureLoad}
                            drawBottomLine={false}
                            monitoredPeriod={monitoredPeriod}
                            currentTime={measureLoadProps.currentTime}
                            barHeightEm={1}
                        />
                        {measureLoadProps.monitoredTv?.loadValues && (
                            <EntryLoadBar
                                key="entry-load"
                                loadValues={measureLoadProps.monitoredTv.loadValues}
                                monitoredPeriod={monitoredPeriod}
                                activePeriods={measureLoadProps.monitoredTv.activePeriods}
                                currentTime={measureLoadProps.currentTime}
                                drawBottomLine={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {measureLoadProps.monitoredTv?.occupancyLoadValues &&
                            measureLoadProps.monitoredTv?.occupancyLoadValues.map(occupancyValues => {
                                return (
                                    <OccupancyLoadBar
                                        key={'occupancy-load-' + occupancyValues.durationInMin}
                                        loadValues={occupancyValues.occupancyLoadValues}
                                        monitoredPeriod={monitoredPeriod}
                                        activePeriods={measureLoadProps.monitoredTv.activePeriods}
                                        currentTime={measureLoadProps.currentTime}
                                        drawBottomLine={true}
                                        barHeightEm={0.5}
                                    />
                                );
                            })}
                    </Flex>
                </Flex>
            </div>
        );
    }

    addToCustomLayerMenu(measureLoadProps: MeasureLoadProps) {
        const measureId = measureLoadProps.monitoredMeasure.measure.measureId.id;
        const result = {
            label: 'Add to custom layer',
            key: 'add-to-custom-layer-menu-' + measureId,
            children: [
                ...CustomLayerUtils.customLayers
                    .filter(customLayer => !CustomLayerUtils.containsMeasure(customLayer, measureId))
                    .map(customLayer => ({
                        key: 'add-to-layer-' + customLayer.id + measureId,
                        label: `Add to: ${customLayer.label}`,
                        onClick: () => this.addToCustomLayerMenuHandleUpdateSection(measureId, customLayer),
                    })),
            ],
        };

        if (result.children.length > 0) {
            return result;
        }
    }

    removeFromCustomLayerMenu(measureId: string) {
        const measureCustomLayers = this.getMeasureCustomLayers(measureId);
        if (measureCustomLayers?.length > 0) {
            return [
                { key: 'divider-cl' + measureId, type: 'divider' },
                {
                    key: 'remove-from-layer-' + measureId,
                    label: 'Remove from custom layer',
                    children: [
                        ...measureCustomLayers.map(customLayer => ({
                            key: 'remove-from-layer-' + customLayer.id + measureId,
                            label: `Remove from: ${customLayer.label}`,
                            onClick: () => this.removeFromCustomLayerMenuHandleUpdateSection(measureId, customLayer),
                        })),
                    ],
                },
            ];
        }
        return [];
    }

    removeFromWorkspaceLayerMenu(measureId: string) {
        if (
            Utils.isExists(this.props.workspaceMeasureIds) &&
            Utils.contains(this.props.workspaceMeasureIds, measureId)
        ) {
            return [
                { key: 'divider-wkl' + measureId, type: 'divider' },
                {
                    key: 'remove-from-workspace-layer' + measureId,
                    label: 'Remove from workspace layer',
                    onClick: () => this.removeFromWorkspaceLayerMenuHandler(measureId),
                },
            ];
        }
        return [];
    }

    removeFromCustomLayerMenuHandleUpdateSection(measureId: string, customLayer: MapLayer) {
        this.props.onClose();
        RemoveFromCustomLayerEvent.fire(customLayer, measureId, MapObjectType.MEASURE);
    }

    removeFromWorkspaceLayerMenuHandler(measureId: string) {
        this.props.onClose();
        RemoveFromWorkspaceLayerEvent.fireRemoveMeasure(measureId);
    }

    actionKey(action: string, measureId: string) {
        return `${action}_${measureId}`;
    }

    handleOnClick = event => {
        const parsedKey = event.key.split('_');
        const action = parsedKey[0].trim();
        //check if is a valid action
        if (!ACTIONS.includes(action)) return;

        const measureId: string = parsedKey[1];
        //sanity check
        const measureLoad = this.props.measures.find(
            measureLoad => measureLoad.monitoredMeasure?.measure?.measureId?.id === measureId,
        );

        //check if is a valid tv
        if (!measureLoad) return;
        //close the context menu first
        this.props.onClose();

        const { id, refLocation, tvSetIds } = MeasureUtils.getTvLocation(measureLoad.monitoredMeasure.measure);
        switch (action) {
            case ACTION_OPEN_EDIT:
                ModalHandler.showRegulationEditor(measureId, 'reg');
                break;
            case ACTION_SLOT_LIST:
                ModalHandler.showSlotList(measureLoad.monitoredMeasure.measure.measureId);
                break;
            case ACTION_OP_LOG:
                ModalHandler.showMeasureOpLog(measureLoad.monitoredMeasure.measure.measureId);
                break;
            case ACTION_INTERACTION_STUDY:
                ModalHandler.showInteractionStudy(measureLoad.monitoredMeasure.measure.measureId);
                break;
            case ACTION_CREATE_CUSTOM_LAYER:
                this.showCustomLayerNamePopup(id);
                break;
            case ACTION_ADD_NOTE:
                OpenTvMonitoredEvents.fire(MonitoredEventType.NOTE, id, refLocation, tvSetIds, [measureId]);
                break;
            case ACTION_ADD_EVENT:
                OpenTvMonitoredEvents.fire(MonitoredEventType.WEATHER_EVENT, id, refLocation, tvSetIds, [measureId]);
                break;
        }
    };

    addToCustomLayerMenuHandleUpdateSection(measureId: string, customLayer: MapLayer) {
        this.props.onClose();
        AddToCustomLayerEvent.fire(customLayer, measureId, MapObjectType.MEASURE);
    }

    showCustomLayerNamePopup(tvName: string) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: [tvName],
                    selected: [tvName],
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };

    handleMeasureLoadClick = (event: MouseEvent<HTMLDivElement>, measureId: string) => {
        event.stopPropagation();
        this.props.onClose();
        ModalHandler.showRegulationEditor(measureId, 'reg', undefined, AuthorisationUtils.isFmpMapUser());
    };
}
